<template>
  <div class="relative">
    <Transition
      mode="out-in"
      enter-active-class="transition-opacity duration-200 ease-out"
      enter-from-class="opacity-0"
      enter-to-class="opacity-100"
      leave-active-class="transition-opacity duration-150 ease-in"
      leave-from-class="opacity-100"
      leave-to-class="opacity-0"
    >
      <StepLoadingSkeleton v-if="isLoading" key="loading" variant="profile" />
      <div v-else key="content" class="p-6 lg:px-8 lg:pt-4 lg:pb-8">
      <ProfileEditForm
        v-if="editing"
        :user="normalizedUser"
        :initial-provinces="provinces"
        :initial-church="churchDetail"
        @cancel="editing = false"
        @saved="onSaved"
      />

      <VerificationPending
        v-else-if="needsVerification"
        :phone-verified="!!normalizedUser.phoneVerified"
        :email-verified="!!normalizedUser.emailVerified"
        :phone-number="normalizedUser.phoneNumber"
        :email="normalizedUser.email"
        :busy="otpSending"
        :busy-type="busyType"
        @verify="onRequestVerification"
        @edit="editing = true"
      />

      <ProfileView
        v-else
        :user="normalizedUser"
        :province-name="provinceName"
        :church-name="churchName"
        :coordinator-name="coordinatorName"
        @edit="editing = true"
        @back="$emit('back')"
      />
      </div>
    </Transition>

    <VerifyOtpModal
      :open="verifyingType !== null"
      :type="verifyingType || 'phone'"
      :client-id="normalizedUser.clientId"
      :doc-type="normalizedUser.docType"
      :document-number="String(normalizedUser.documentNumber || '')"
      :masked-destination="otpMaskedDestination"
      :destination-fallback="
        verifyingType === 'phone'
          ? normalizedUser.phoneNumber
          : normalizedUser.email
      "
      @close="verifyingType = null"
      @update:open="(v) => { if (!v) verifyingType = null; }"
      @verified="onVerified"
    />
  </div>
</template>

<script>
  import axios from "axios";
  import { createToast } from "mosha-vue-toastify";
  import VerificationPending from "./VerificationPending.vue";
  import ProfileView from "./ProfileView.vue";
  import ProfileEditForm from "./ProfileEditForm.vue";
  import VerifyOtpModal from "./VerifyOtpModal.vue";
  import StepLoadingSkeleton from "../StepLoadingSkeleton.vue";

  export default {
    name: "UserProfile",
    components: {
      StepLoadingSkeleton,
      VerificationPending,
      ProfileView,
      ProfileEditForm,
      VerifyOtpModal,
    },
    props: {
      authenticatedUser: { type: Object, required: true },
    },
    emits: ["update", "back"],
    data() {
      return {
        editing: false,
        verifyingType: null,
        otpSending: false,
        busyType: "",
        otpMaskedDestination: "",
        isLoading: !!(this.authenticatedUser?.churchClientId),
        provinceName: "",
        churchName: "",
        coordinatorName: "",
        provinces: [],
        churchDetail: null,
      };
    },
    computed: {
      normalizedUser() {
        const u = this.authenticatedUser || {};
        return {
          ...u,
          clientId: u.clientId ?? u.ClientId,
          churchClientId: u.churchClientId ?? u.ChurchClientID ?? u.ChurchClientId,
          emailVerified: u.emailVerified ?? u.EmailVerified ?? false,
          phoneVerified: u.phoneVerified ?? u.PhoneVerified ?? false,
        };
      },
      needsVerification() {
        const u = this.normalizedUser;
        return !u.phoneVerified || !u.emailVerified;
      },
    },
    watch: {
      "normalizedUser.churchClientId": {
        immediate: true,
        handler(churchClientId) {
          this.loadChurchInfo(churchClientId);
        },
      },
    },
    methods: {
      async loadChurchInfo(churchClientId) {
        if (!churchClientId) {
          this.provinceName = "";
          this.churchName = "";
          this.coordinatorName = "";
          this.provinces = [];
          this.churchDetail = null;
          return;
        }
        try {
          this.isLoading = true;
          const { data } = await axios.get(`Churches/ById/${churchClientId}`);
          if (!data) return;
          this.churchDetail = data;
          this.churchName = data.churchName || "";
          this.coordinatorName = data.coordinatorName || "";
          if (data.provinceId) {
            try {
              const { data: provinces } = await axios.get(`Provinces/all`);
              this.provinces = provinces || [];
              const p = this.provinces.find(
                (x) => x.provinceId === data.provinceId
              );
              this.provinceName = p?.provinceName || "";
            } catch (e) {
              console.log(e);
            }
          }
        } catch (err) {
          console.log(err);
        } finally {
          this.isLoading = false;
        }
      },
      async onRequestVerification(type) {
        if (this.otpSending) return;
        const u = this.normalizedUser;
        if (!u.clientId) return;
        this.busyType = type;
        this.otpSending = true;
        try {
          const endpoint =
            type === "phone"
              ? "subscriber-auth/phone/send-verification-code"
              : "subscriber-auth/email/send-verification-code";
          const { data } = await axios.post(endpoint, {
            clientId: u.clientId,
            docType: u.docType,
            documentNumber: String(u.documentNumber || "").trim(),
          });
          if (data.success) {
            this.otpMaskedDestination =
              type === "phone" ? data.maskedPhone || "" : data.maskedEmail || "";
            this.verifyingType = type;
            createToast(
              type === "phone"
                ? "Te enviamos un código por SMS."
                : "Te enviamos un código por correo.",
              {
                position: "top-center",
                type: "success",
                transition: "zoom",
              }
            );
          } else {
            createToast(data.errorMessage || "No se pudo enviar el código.", {
              position: "top-center",
              type: "danger",
              transition: "zoom",
            });
          }
        } catch (err) {
          const body = err.response?.data;
          const msg =
            (typeof body === "string" && body) ||
            body?.errorMessage ||
            body?.title ||
            "No se pudo enviar el código.";
          createToast(msg, {
            position: "top-center",
            type: "danger",
            transition: "zoom",
          });
        } finally {
          this.otpSending = false;
          this.busyType = "";
        }
      },
      onVerified({ type }) {
        const updated = { ...this.normalizedUser };
        if (type === "phone") updated.phoneVerified = true;
        else updated.emailVerified = true;
        this.verifyingType = null;
        this.otpMaskedDestination = "";
        this.$emit("update", updated);
        createToast(
          type === "phone" ? "¡Teléfono verificado!" : "¡Email verificado!",
          {
            position: "top-center",
            type: "success",
            transition: "zoom",
          }
        );
      },
      onSaved({ updatedUser }) {
        this.editing = false;
        this.$emit("update", updatedUser);
      },
    },
  };
</script>
